import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import {
	BusyIndicator,
	ChangeTriggerKind,
	PersonAvatar,
	PipeSeparator,
} from "client/Components";
import {
	ClientListItem,
	getClients
} from "client/Api/Workspace/LiveInformation/NedapOns/nedapOnsLiveInformation";
import { LiveTileContent } from "../LiveTileContent";
import { DimensionToRender } from "../../RenderState/common";
import { ListItem } from "../ListItem";
import { DestinationWindow } from "client/Api/Apps/common";
import { prefixNameWithGender } from "./misc";
import {
	AlertManager,
	createRemoteDataSource,
	LazyLoadCursorKind,
	useAlerts
} from "client/Hooks";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { generateTrackingId, isNotNilOrEmpty } from "client/Misc/utils";
import { navigateToNedapOnsApp } from "client_legacy/Apps/appNavigation";

export type NedapOnsClientsProps = {
	connectionId: number;
	destinationWindow: DestinationWindow;
	dimensionToRender: DimensionToRender;
	environmentAppId: number;
	addAnalyticsEvent(): void;
};

export const NedapOnsClients = (props: NedapOnsClientsProps): ReactElement => {
	const {
		connectionId,
		dimensionToRender,
		destinationWindow,
		environmentAppId,
		addAnalyticsEvent
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});
	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);

	const isWideView: boolean = dimensionToRender.dimension.columnSpan >= 6;

	const {
		currentItems,
		getLoadingState,
		hasMoreOnServer,
		reload
	} = useClientsDataSource({
		globalContext: {
			connectionId
		},
		searchTerm: searchTerm.value,
		desiredAmountOfItems: desiredAmountOfItems
	});

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems(x => x + PAGE_SIZE);
		},
		[]
	);

	const searchTermChangeHandler = useCallback(
		(newValue: string, version: Guid): void => {
			setSearchTerm({
				value: newValue,
				version
			});

			setDesiredAmountOfItems(PAGE_SIZE);
		},
		[]
	);

	const hasError: boolean = getLoadingState() === "error";
	const isLoading: boolean = getLoadingState() === "loading";
	const hasItems: boolean = currentItems.length > 0;

	const navigationHandler = useCallback(
		(): void => {
			addAnalyticsEvent();

			navigateToNedapOnsApp({
				id: environmentAppId,
				destinationWindow,
				view: "clients"
			});

		},
		[
			addAnalyticsEvent,
			environmentAppId,
			destinationWindow
		]
	);

	const renderContent = (): ReactElement | null => {
		if (hasError) {
			return (
				<LiveTileContent.NoResults
					trackingId="has-error"
					icon="gear"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					onRetry={reload}
				/>
			);
		}

		if (!hasItems) {
			if (isLoading) {
				return null;
			}

			if (isNotNilOrEmpty(searchTerm.value)) {
				return (
					<LiveTileContent.Message
						text={localize("_YouDontHaveAnyClients")}
					/>
				);
			}

			return (
				<LiveTileContent.Message
					text={localize("_NoResultsFound")}
				/>
			);
		}

		return (
			<LiveTileContent.Scrollable>
				<LiveTileContent.List>
					{currentItems.map((x: ClientListItem) => (
						<ClientsListItem
							key={x.id}
							item={x}
							isWideView={isWideView}
							destinationWindow={destinationWindow}
							onClick={addAnalyticsEvent}
						/>
					))}
				</LiveTileContent.List>
				{
					hasMoreOnServer && (
						<LiveTileContent.LoadMoreButton
							onClick={loadMoreHandler}
						/>
					)
				}
			</LiveTileContent.Scrollable>
		);
	};

	return(
		<>
			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<LiveTileContent.SearchInput
						onChange={searchTermChangeHandler}
						placeholder={localize("_Search")}
						trigger={{ kind: ChangeTriggerKind.Debounced }}
					/>
					<LiveTileContent.BusyIndicatorContainer>
						{isLoading && (
							<BusyIndicator />
						)}
						{renderContent()}
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={localize("_Clients")}
				onClick={navigationHandler}
			/>
		</>
	);
};
NedapOnsClients.displayName = "NedapOnsClients";

type ClientsListItemProps = {
	isWideView: boolean;
	item: ClientListItem;
	destinationWindow: DestinationWindow;
	onClick(): void;
};

export const ClientsListItem = (props: ClientsListItemProps): ReactElement => {
	const {
		item,
		isWideView,
		destinationWindow,
		onClick
	} = props;

	const alertManager: AlertManager = useAlerts();

	const listItemClickHandler = useCallback(
		(): void => {
			onClick();

			if (item.clientOverviewUrl) {
				navigateTo(
					item.clientOverviewUrl,
					destinationWindow === DestinationWindow.NewWindow
				);
			} else {
				alertManager.showInfo(localize("_TheClientIsNotLinkedToOnsAdministration"));
			}
		},
		[
			alertManager,
			destinationWindow,
			item.clientOverviewUrl,
			onClick
		]
	);

	return (
		<ListItem
			isHoverable
			onClick={listItemClickHandler}
			trackingId={generateTrackingId("client", `|${item.id}`)}
		>
			{isWideView && (
				<ListItem.Icon>
					<PersonAvatar
						initials={item.initials}
						size="small"
					/>
				</ListItem.Icon>
			)}
			<ListItem.Content>
				<ListItem.Row>
					<ListItem.Title>
						{prefixNameWithGender(item.name, item.gender)}
					</ListItem.Title>
				</ListItem.Row>

				<ListItem.Row>
					<ListItem.Info>
						{item.address}
						{isWideView && item.identificationCode && (
							<>
								{item.address && (
									<PipeSeparator />
								)}
								{item.identificationCode}
							</>
						)}
					</ListItem.Info>
				</ListItem.Row>
			</ListItem.Content>
		</ListItem>
	);
};

ClientsListItem.displayName = "ClientsListItem";

const PAGE_SIZE: number = 10;

type GlobalContext = {
	connectionId: number;
};

const useClientsDataSource = createRemoteDataSource<ClientListItem, undefined, GlobalContext>()
	.setConfiguration({
		hasSearch: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipTake
	})
	.getHook({
		performRequest: async (options) => {
			const {
				globalContext,
				skip,
				amount,
				searchTerm
			} = options;

			return await getClients(
				globalContext.connectionId,
				skip,
				amount,
				searchTerm
			);
		}
	});
